import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { GraduationCap, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err, "Could not submit request."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="px-8 lg:px-16 py-8">
        <Link to="/" className="flex items-center gap-3 w-fit" data-testid="forgot-logo">
          <div className="w-9 h-9 bg-[#E25822] flex items-center justify-center">
            <GraduationCap size={22} weight="bold" color="#FFFFFF" />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">UzviS Learning</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 -mt-16">
        <div className="max-w-md w-full page-fade">
          {submitted ? (
            <div data-testid="forgot-success">
              <CheckCircle size={42} weight="duotone" color="#4A6B53" className="mb-5" />
              <div className="eyebrow mb-3">Check your inbox</div>
              <h1 className="font-heading text-4xl font-semibold tracking-tight leading-none mb-3">
                Reset link issued.
              </h1>
              <p className="text-[#5C5C58] mb-8">
                If <span className="font-semibold">{email}</span> is registered with UzviS Learning, a password
                reset link has been issued. The link is valid for one hour and can only be used once.
              </p>
              <div className="border-l-2 border-[#E25822] bg-[#E25822]/[0.04] px-4 py-3 text-xs text-[#3D3D38] mb-8 font-mono">
                Internal LMS: in this environment the reset link is also written to the backend server log
                (visible to your admin).
              </div>
              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 bg-[#E25822] hover:bg-[#C44E1A] text-white font-medium"
                data-testid="forgot-back-to-login"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="eyebrow mb-3">Account recovery</div>
              <h1 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight leading-none mb-3">
                Forgot your password?
              </h1>
              <p className="text-[#5C5C58] mb-10">
                Enter your work email and we&apos;ll send a single-use reset link, valid for 1 hour.
              </p>

              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#3D3D38]">
                    Work email
                  </Label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 h-12 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
                    placeholder="you@company.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                {error && (
                  <div
                    className="text-sm text-[#993333] border-l-2 border-[#993333] pl-3 py-1 bg-red-50"
                    data-testid="forgot-error"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[#E25822] hover:bg-[#C44E1A] text-white rounded-lg text-base font-medium"
                  data-testid="forgot-submit-button"
                >
                  {submitting ? "Sending..." : "Send reset link"}
                </Button>
              </form>

              <div className="mt-8 text-sm text-[#5C5C58]">
                Remembered it?{" "}
                <Link
                  to="/login"
                  className="text-[#E25822] font-semibold hover:underline"
                  data-testid="forgot-login-link"
                >
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
